import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, Check, Loader2, Flame, Trophy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Close-of-shift ritual: pre-close checklist (required items force the
 * manager to actually tick "все продажи зафиксированы" etc), auto stats
 * pulled from CRM + invoices, one free-form note field, and a "close"
 * button that fires the Telegram report to management.
 */
export default function ShiftEndModal({ shift, onClose }) {
  const [meta, setMeta] = useState(null);
  const [done, setDone] = useState(new Set());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // {plan_met, streak, auto_stats}

  useEffect(() => {
    axios.get(`${API}/admin/shift/checklists`, { withCredentials: true })
      .then(r => setMeta(r.data)).catch(() => {});
  }, []);

  const items = useMemo(() => {
    if (!meta || !shift) return [];
    return meta[shift.mode]?.end || [];
  }, [meta, shift]);

  const requiredIds = useMemo(() => items.filter(i => i.required).map(i => i.id), [items]);
  const missingRequired = requiredIds.some(id => !done.has(id));

  const toggle = (id) => setDone(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const stats = shift.auto_stats || {};
  const goal = shift.goal || {};
  const revPct = goal.revenue > 0
    ? Math.round((stats.revenue || 0) / goal.revenue * 100) : 0;

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/shift/close`, {
        end_checklist_done: Array.from(done),
        notes: note,
      }, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось закрыть смену");
      setSaving(false);
    }
  };

  // After successful close — show a small "goodbye" screen with the streak.
  if (result) {
    return (
      <div style={backdrop} onClick={() => onClose(true)}>
        <div data-testid="shift-end-success" onClick={e => e.stopPropagation()}
          style={{ ...modal, maxWidth: 460, textAlign: "center" }}>
          {result.plan_met ? (
            <>
              <div style={{ fontSize: 48, marginBottom: 6 }}>🎉</div>
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#0A0A0A", marginBottom: 6 }}>
                План выполнен!
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6,
                             color: "#B45309", fontFamily: "Manrope", fontWeight: 700,
                             fontSize: 14, marginBottom: 8 }}>
                <Flame size={16} /> Серия: {result.streak}
              </div>
              {result.streak >= 3 && (
                <div style={{ background: "#fef3c7", border: "1px solid #fde68a",
                               borderRadius: 8, padding: 12, marginTop: 8,
                               fontFamily: "Manrope", fontSize: 13, color: "#7C2D12" }}>
                  🎁 3 смены подряд с планом — вы заслужили небольшой бонус от руководства.
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: 48, marginBottom: 6 }}>🌙</div>
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#0A0A0A", marginBottom: 8 }}>
                Смена закрыта
              </div>
              <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b" }}>
                Отчёт отправлен руководству. Завтра — новая попытка выйти на план.
              </div>
            </>
          )}
          <button data-testid="shift-end-done"
            onClick={() => onClose(true)}
            style={{ marginTop: 20, background: "linear-gradient(135deg,#C5A059,#D4B878)",
                      color: "#0A0A0A", border: "none", borderRadius: 6,
                      padding: "10px 24px", fontFamily: "Manrope", fontWeight: 700,
                      fontSize: 13, cursor: "pointer" }}>
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={backdrop} onClick={() => onClose(false)}>
      <div data-testid="shift-end-modal" onClick={e => e.stopPropagation()} style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24, color: "#0A0A0A" }}>
            Закрытие {shift.mode === "online" ? "ONLINE" : "OFFLINE"}-смены
          </div>
          <button onClick={() => onClose(false)} style={closeBtn}><X size={18} /></button>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b", marginBottom: 14 }}>
          Перед закрытием проверьте главное. Обязательные пункты помечены звёздочкой.
        </div>

        {/* Auto stats preview */}
        <div style={{ background: "#faf9f5", border: "1px solid #efeee9",
                       borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
            <Stat label="Лиды" value={stats.leads_processed || 0}
                   sub={`из ${stats.leads_new || 0} новых`} />
            <Stat label="Продажи" value={stats.sales_count || 0}
                   sub={`цель ${goal.sales_count || 0}`} />
            <Stat label="Выручка"
                   value={((stats.revenue || 0) / 1000).toFixed(0) + "к ₸"}
                   sub={goal.revenue ? "цель " + (goal.revenue / 1000).toFixed(0) + "к" : ""} />
            <Stat label="Факт"
                   value={revPct + "%"} tone={revPct >= 100 ? "#059669" : "#B45309"}
                   sub={revPct >= 100 ? "🔥 план" : "не дотянули"} />
          </div>
        </div>

        {/* Checklist */}
        {items.map((it) => (
          <label key={it.id} data-testid={`shift-end-check-${it.id}`}
            onClick={() => toggle(it.id)}
            style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "8px 12px", marginBottom: 4, borderRadius: 8,
              border: done.has(it.id) ? "1px solid #C5A059" : "1px solid #e5e7eb",
              background: done.has(it.id) ? "rgba(197,160,89,0.05)" : "white",
              cursor: "pointer",
            }}>
            <div style={{
              width: 18, height: 18, borderRadius: 4, marginTop: 1,
              border: "2px solid " + (done.has(it.id) ? "#C5A059" : "#d1d5db"),
              background: done.has(it.id) ? "#C5A059" : "white",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {done.has(it.id) && <Check size={12} color="white" />}
            </div>
            <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#0A0A0A", lineHeight: 1.4 }}>
              {it.text} {it.required && <span style={{ color: "#DC2626" }}>*</span>}
            </div>
          </label>
        ))}

        {/* Notes */}
        <div style={{ marginTop: 12 }}>
          <div style={lbl}>Что было главным результатом дня?</div>
          <textarea data-testid="shift-end-note" value={note}
            onChange={e => setNote(e.target.value)}
            rows={2} placeholder="Напр.: получилось закрыть сложную клиентку на пальто со скидкой 10%"
            style={{ ...inp, resize: "vertical" }} />
        </div>

        {error && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#fef2f2",
                         border: "1px solid #fecaca", borderRadius: 6,
                         color: "#7f1d1d", fontFamily: "Manrope", fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8 }}>
          <button data-testid="shift-end-cancel" onClick={() => onClose(false)}
            style={{ background: "white", border: "1px solid #d1d5db", borderRadius: 6,
                      padding: "10px 16px", cursor: "pointer",
                      fontFamily: "Manrope", fontSize: 12, fontWeight: 600, color: "#374151" }}>
            Отмена
          </button>
          <button data-testid="shift-end-confirm" onClick={submit}
            disabled={saving || missingRequired}
            title={missingRequired ? "Отметьте обязательные пункты" : ""}
            style={{ background: saving || missingRequired ? "#e5e7eb" : "#0A0A0A",
                      color: saving || missingRequired ? "#94a3b8" : "#D4B878",
                      border: "none", borderRadius: 6, padding: "10px 20px",
                      cursor: (saving || missingRequired) ? "not-allowed" : "pointer",
                      fontFamily: "Manrope", fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
                      display: "inline-flex", alignItems: "center", gap: 6 }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Trophy size={13} />}
            Закрыть смену и отправить отчёт
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: "#8a8a89", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, fontWeight: 700, color: tone || "#0A0A0A" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const backdrop = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200,
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "24px 16px", overflowY: "auto",
};
const modal = {
  background: "white", borderRadius: 12, maxWidth: 620, width: "100%",
  padding: 24, margin: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
};
const closeBtn = { background: "none", border: "none", cursor: "pointer", color: "#888" };
const inp = { width: "100%", background: "white", border: "1px solid #e5e7eb",
              borderRadius: 6, padding: "9px 11px", fontFamily: "Manrope", fontSize: 13,
              outline: "none" };
const lbl = { display: "block", fontFamily: "Manrope", fontSize: 11, fontWeight: 700,
              color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em",
              marginBottom: 5 };
